declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    _paq?: any[];
  }
}

// Analytics utility functions
export const trackEvent = (
  category: string,
  action: string,
  label?: string,
  value?: number
) => {
  // Google Analytics
  if (window.gtag) {
    window.gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value
    });
  }

  // Matomo Analytics
  if (window._paq) {
    window._paq.push(['trackEvent', category, action, label, value]);
  }
};

// Content interaction events
export const trackContentView = (contentType: string, content: string) => {
  trackEvent('Content', 'View', `${contentType}: ${content}`);
};

export const trackNextContent = (contentType: string) => {
  trackEvent('Navigation', 'Next Content', contentType);
};

export const trackLike = (contentType: string, content: string) => {
  trackEvent('Engagement', 'Like', `${contentType}: ${content}`);
};

export const trackShare = (contentType: string, content: string, platform: string) => {
  trackEvent('Social', 'Share', `${contentType}: ${content} via ${platform}`);
};

// Achievement events
export const trackAchievementUnlock = (achievementTitle: string, points: number) => {
  trackEvent('Achievement', 'Unlock', achievementTitle, points);
};

// Challenge events
export const trackChallengeComplete = (challenge: string) => {
  trackEvent('Challenge', 'Complete', challenge);
};

// Streak events
export const trackStreakUpdate = (days: number) => {
  trackEvent('Engagement', 'Streak Update', 'Days', days);
};

// Navigation events
export const trackNavigation = (from: string, to: string) => {
  trackEvent('Navigation', 'Page Change', `${from} -> ${to}`);
};

// User events
export const trackUserAction = (action: string, details?: string) => {
  trackEvent('User', action, details);
};

// Error events
export const trackError = (error: string, context: string) => {
  trackEvent('Error', context, error);
};

// Page view tracking (for SPA navigation)
export const trackPageView = (title: string, path?: string) => {
  // Google Analytics
  if (window.gtag) {
    window.gtag('config', import.meta.env.VITE_GA_MEASUREMENT_ID, {
      page_title: title,
      page_path: path || window.location.pathname,
    });
  }

  // Matomo Analytics
  if (window._paq) {
    window._paq.push(['setCustomUrl', path || window.location.pathname]);
    window._paq.push(['setDocumentTitle', title]);
    window._paq.push(['trackPageView']);
  }
};

// Custom dimension tracking for Matomo
export const setCustomDimension = (id: number, value: string) => {
  if (window._paq) {
    window._paq.push(['setCustomDimension', id, value]);
  }
};

// User ID tracking
export const setUserId = (userId: string) => {
  // Google Analytics
  if (window.gtag) {
    window.gtag('config', import.meta.env.VITE_GA_MEASUREMENT_ID, {
      user_id: userId
    });
  }

  // Matomo Analytics
  if (window._paq) {
    window._paq.push(['setUserId', userId]);
  }
};

// Track outbound links
export const trackOutboundLink = (url: string) => {
  if (window._paq) {
    window._paq.push(['trackLink', url, 'link']);
  }
  trackEvent('Outbound', 'Click', url);
};

// Track downloads
export const trackDownload = (url: string, fileName?: string) => {
  if (window._paq) {
    window._paq.push(['trackLink', url, 'download']);
  }
  trackEvent('Download', 'File', fileName || url);
};

// Track site search
export const trackSiteSearch = (keyword: string, category?: string, resultsCount?: number) => {
  if (window._paq) {
    window._paq.push(['trackSiteSearch', keyword, category, resultsCount]);
  }
  trackEvent('Search', 'Query', keyword, resultsCount);
};

// Track form interactions
export const trackFormStart = (formName: string) => {
  trackEvent('Form', 'Start', formName);
};

export const trackFormSubmit = (formName: string, success: boolean = true) => {
  trackEvent('Form', success ? 'Submit Success' : 'Submit Failed', formName);
};

// Track scroll depth
export const trackScrollDepth = (percentage: number) => {
  trackEvent('Engagement', 'Scroll Depth', `${percentage}%`, percentage);
};

// Track time on page
export const trackTimeOnPage = (seconds: number, page: string) => {
  trackEvent('Engagement', 'Time on Page', page, seconds);
};